import { AmhsStockerFromLayout3D, AmhsLoadPortFromLayout3D, AmhsEquipmentFromLayout3D } from './AmhsStockerFromLayout3D';
import { EquipmentFloorMesh } from './EquipmentMeshes3D';
import {
  DualRailStraight3D,
  DualRailCurve3D,
  BlenderSwitch3D,
  CircleJunctionTurntableMesh3D,
} from './DualRailTrack3D';
import { LayoutElement } from '../../types';
import { getOhtPivot, getOhtPivotWorld } from '../../utils/ohtPivot';
import {
  getConnectorShiftState,
  getConnectorSolidLegLocalY,
} from '../../utils/connectorRail';
import { connectorShiftStore } from '../../store/connectorShiftStore';
import { junctionTurntableStore } from '../../store/junctionTurntableStore';
import { getTurntableSettledAngleDeg } from '../../utils/junctionTurntableSim';
import {
  ALL_RAIL_TYPES,
  getCurveSweepAngle,
  getCurveSweepSign,
  isConnectorRailType,
  isCurveRailType,
  isJunctionRailType,
  resolveRailType,
  getCircleJunctionMaxPorts,
  getCircleJunctionSpokesLocal,
  getCircleJunctionRingRadius,
  getStraightRailCircleJunctionReach,
  resolveStraightRail3DSpan,
} from '../../utils/topology';
import {
  getElement3DExtrusionDepth,
  getElement3DGroupY,
  TRACK_3D_MIN_THICKNESS,
  TRACK_3D_THICKNESS,
  isDxfEquipmentLayoutElement,
} from '../../utils/layout3d';
import { useRailElevation } from './railElevationContext';
import { FACTORY_3D_PALETTE } from '../../utils/factory3dStyles';
import { createFlatLabelTexture } from '../../utils/label3dTexture';
import { FrameVehicle } from '../../hooks/useSimulationFrames';
import { metersToEffectiveWorldUnits } from '../../utils/measurement';
import { OhtVehicle3DModel } from './OhtVehicle3DModel';
import React, { Suspense, useEffect, useMemo, useSyncExternalStore } from 'react';
import * as THREE from 'three';

const EMPTY_LAYOUT_ELEMENTS: Record<string, LayoutElement> = {};

type Props = {
  element: LayoutElement;
  isConflict?: boolean;
  storedFoupsCount?: number;
  simulationFrames?: FrameVehicle[];
  /**
   * Full layout map for OHT rail-snap and junction spoke connectivity.
   * Passed from the parent so this leaf does not re-subscribe to every
   * layout.elements change across the whole scene.
   */
  layoutElements?: Record<string, LayoutElement>;
  /** When true, straight dual-rail mesh is drawn by RailInstancedLayer3D. */
  skipStraightRailMesh?: boolean;
  floorPlanScaleFactor?: number;
  dxfUnitsPerMeter?: number;
  hasFloorCalibration?: boolean;
};

/** Match 2D CanvasElement stroke width so rails read as real 3D beams. */
function getTrackStrokeWidth(
  el: LayoutElement,
  floorPlanScaleFactor?: number,
  dxfUnitsPerMeter?: number
): number {
  const thicknessMeters = Number(el.customProperties?.thicknessMeters);
  if (Number.isFinite(thicknessMeters) && thicknessMeters > 0) {
    return Math.max(
      TRACK_3D_MIN_THICKNESS,
      metersToEffectiveWorldUnits(thicknessMeters, floorPlanScaleFactor, dxfUnitsPerMeter)
    );
  }
  return TRACK_3D_THICKNESS;
}

const getCurveRadius = (width: number, height: number) => Math.max(0.001, Math.min(width, height));

const RAIL_LIFT = 0.5;
const LINE_ASPECT_THRESHOLD = 8;

function isLineLikeRectangle(element: LayoutElement, w: number, h: number): boolean {
  if (element.type !== 'rectangle') return false;
  if (element.customProperties?.annotationShape === 'line') return true;
  const min = Math.min(w, h);
  const max = Math.max(w, h);
  return min > 0 && max / min >= LINE_ASPECT_THRESHOLD;
}

function FloorRect3D({
  w,
  h,
  depth,
  fill,
  opacity,
}: {
  w: number;
  h: number;
  depth: number;
  fill: string;
  opacity: number;
}) {
  return (
    <mesh position={[w / 2, depth / 2, h / 2]}>
      <boxGeometry args={[w, depth, h]} />
      <meshBasicMaterial
        color={fill}
        transparent={opacity < 1}
        opacity={opacity}
        toneMapped={false}
      />
    </mesh>
  );
}

function LineRect3D({
  w,
  h,
  depth,
  fill,
  opacity,
}: {
  w: number;
  h: number;
  depth: number;
  fill: string;
  opacity: number;
}) {
  const strokeW = Math.max(TRACK_3D_MIN_THICKNESS, Math.min(w, h, TRACK_3D_THICKNESS));
  const horizontal = w >= h;
  return (
    <mesh position={[w / 2, depth / 2, h / 2]}>
      <boxGeometry args={[horizontal ? w : strokeW, depth, horizontal ? strokeW : h]} />
      <meshBasicMaterial
        color={fill}
        transparent={opacity < 1}
        opacity={opacity}
        toneMapped={false}
      />
    </mesh>
  );
}

function TextLabel3D({
  text,
  capHeight,
  fill,
  opacity,
}: {
  text: string;
  capHeight: number;
  fill: string;
  opacity: number;
}) {
  const { texture, planeW, planeH } = useMemo(
    () => createFlatLabelTexture(text, fill, capHeight),
    [text, fill, capHeight]
  );

  useEffect(() => () => texture.dispose(), [texture]);

  return (
    <group position={[0, 1.1, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[planeW / 2, 0, 0]} renderOrder={8}>
        <planeGeometry args={[planeW, planeH]} />
        <meshBasicMaterial
          map={texture}
          transparent
          opacity={opacity}
          alphaTest={0.02}
          toneMapped={false}
          depthTest
          depthWrite={false}
          polygonOffset
          polygonOffsetFactor={-4}
          polygonOffsetUnits={-4}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

function OhtVehicle3D({
  element,
  w,
  h,
  depth,
  fill,
  opacity,
}: {
  element: LayoutElement;
  w: number;
  h: number;
  depth: number;
  fill: string;
  opacity: number;
}) {
  const groupY = getElement3DGroupY(element, undefined, useRailElevation());
  const isLoaded = element.customProperties?.loaded === true || element.customProperties?.loaded === 'true';

  const { pivotX, pivotY } = getOhtPivot(element, w, h);

  return (
    <Suspense fallback={<FloorRect3D w={w} h={h} depth={depth} fill={fill} opacity={opacity} />}>
      <OhtVehicle3DModel
        w={w}
        h={h}
        depth={depth}
        opacity={opacity}
        paused={true}
        groupY={groupY}
        isLoaded={isLoaded}
        pivotX={pivotX}
        pivotY={pivotY}
      />
    </Suspense>
  );
}

interface RailBeam3DProps {
  p1: [number, number, number];
  p2: [number, number, number];
  thickness: number;
  height: number;
  opacity: number;
  glow?: 'done' | 'prog' | 'pend' | 'hold';
}

const RailBeam3D: React.FC<RailBeam3DProps> = ({
  p1,
  p2,
  thickness,
  height,
  opacity,
  glow = 'done',
}) => {
  const [x1, y1, z1] = p1;
  const [x2, , z2] = p2;

  const dx = x2 - x1;
  const dz = z2 - z1;
  const len = Math.max(0.001, Math.sqrt(dx * dx + dz * dz));
  const cx = (x1 + x2) / 2;
  const cy = y1;
  const cz = (z1 + z2) / 2;
  const angle = -Math.atan2(dz, dx);

  return (
    <group position={[cx, cy, cz]} rotation={[0, angle, 0]}>
      <DualRailStraight3D
        length={len}
        trackWidth={thickness}
        depth={height}
        opacity={opacity}
        glow={glow}
      />
    </group>
  );
};

/**
 * H-connector: legs on each host rail joined by a crossbar, plus the movable
 * segment that ferries an OHT across. The generic straight-rail path would
 * otherwise draw a single beam stranded midway between the two tracks.
 */
const ConnectorRail3D: React.FC<{
  element: LayoutElement;
  w: number;
  h: number;
  depth: number;
  opacity: number;
  strokeW: number;
}> = ({ element, w, h, depth, opacity, strokeW }) => {
  const solidProgress = useSyncExternalStore(
    (onStoreChange) => connectorShiftStore.subscribe(onStoreChange),
    () => connectorShiftStore.getSolidProgress(element.id, getConnectorShiftState(element)),
    () => (getConnectorShiftState(element) === 'shifted' ? 1 : 0)
  );

  const solidZ = getConnectorSolidLegLocalY(element, solidProgress);
  // The 2D view dashes the fixed frame; fade it here so the movable segment reads.
  const guideOpacity = opacity * 0.45;

  return (
    <group>
      <RailBeam3D
        p1={[0, RAIL_LIFT, 0]}
        p2={[w, RAIL_LIFT, 0]}
        thickness={strokeW}
        height={depth}
        opacity={guideOpacity}
        glow="pend"
      />
      <RailBeam3D
        p1={[0, RAIL_LIFT, h]}
        p2={[w, RAIL_LIFT, h]}
        thickness={strokeW}
        height={depth}
        opacity={guideOpacity}
        glow="pend"
      />
      <RailBeam3D
        p1={[w / 2, RAIL_LIFT, 0]}
        p2={[w / 2, RAIL_LIFT, h]}
        thickness={strokeW * 0.85}
        height={depth * 0.85}
        opacity={guideOpacity}
        glow="prog"
      />
      <RailBeam3D
        p1={[0, RAIL_LIFT, solidZ]}
        p2={[w, RAIL_LIFT, solidZ]}
        thickness={strokeW * 1.45}
        height={depth * 1.15}
        opacity={opacity}
        glow="hold"
      />
      <group position={[w / 2, RAIL_LIFT + depth * 0.55, solidZ]}>
        <BlenderSwitch3D size={strokeW * 2.2} depth={depth * 0.75} divert={solidProgress} />
      </group>
    </group>
  );
};

interface OverheadRail3DProps {
  element: LayoutElement;
  layoutRails: LayoutElement[];
  w: number;
  h: number;
  depth: number;
  opacity: number;
  strokeW: number;
  curveMesh: any;
  junctionLines: any;
  skipStraightRailMesh?: boolean;
}

const SchematicTurntable3D: React.FC<{
  element: LayoutElement;
  layoutRails: LayoutElement[];
  cx: number;
  cy: number;
  ringR: number;
  junctionW: number;
  junctionH: number;
  depth: number;
  strokeW: number;
  opacity: number;
  spokeAngles?: number[];
}> = ({ element, layoutRails, cx, cy, ringR, junctionW, junctionH, depth, strokeW, opacity, spokeAngles }) => {
  const settled = getTurntableSettledAngleDeg(element, layoutRails);
  const hubAngleDeg = useSyncExternalStore(
    (cb) => junctionTurntableStore.subscribe(cb),
    () => Math.round(junctionTurntableStore.getHubAngle(element.id, settled) * 2) / 2,
    () => settled
  );
  const divert = Math.min(1, Math.abs(((hubAngleDeg - settled + 540) % 360) - 180) / 90);

  return (
    <group position={[cx, RAIL_LIFT, cy]}>
      <CircleJunctionTurntableMesh3D
        ringR={ringR}
        junctionW={junctionW}
        junctionH={junctionH}
        depth={depth}
        trackWidth={strokeW}
        hubAngleDeg={hubAngleDeg}
        opacity={opacity}
        divert={divert}
        spokeAngles={spokeAngles}
      />
    </group>
  );
};

/** Blender-style dual-rail + neon glow (create_track.py). */
const OverheadRail3D: React.FC<OverheadRail3DProps> = ({
  element,
  layoutRails,
  depth,
  opacity,
  strokeW,
  curveMesh,
  junctionLines,
  skipStraightRailMesh = false,
}) => {
  const junctionReach = useMemo(
    () => getStraightRailCircleJunctionReach(element, layoutRails, strokeW),
    [element, layoutRails, strokeW]
  );
  const straightSpan = useMemo(
    () => resolveStraightRail3DSpan(element, layoutRails, strokeW),
    [element, layoutRails, strokeW]
  );

  if (curveMesh) {
    return (
      <group position={[0, RAIL_LIFT, 0]}>
        <DualRailCurve3D
          radius={curveMesh.radius}
          sweepRad={curveMesh.angle}
          trackWidth={Math.max(strokeW, TRACK_3D_MIN_THICKNESS)}
          depth={depth}
          opacity={opacity}
          glow="done"
          steps={12}
          junctionReachStart={junctionReach.extendStart}
          junctionReachEnd={junctionReach.extendEnd}
        />
      </group>
    );
  }

  if (junctionLines) {
    return (
      <group>
        {junctionLines.lines.map((pts: [number, number, number][], i: number) => (
          <RailBeam3D
            key={i}
            p1={pts[0]!}
            p2={pts[1]!}
            thickness={strokeW}
            height={depth}
            opacity={opacity}
            glow="done"
          />
        ))}
        {junctionLines.isTurntable && (
          <SchematicTurntable3D
            element={element}
            layoutRails={layoutRails}
            cx={junctionLines.cx}
            cy={junctionLines.cy}
            ringR={junctionLines.ringR}
            junctionW={junctionLines.junctionW}
            junctionH={junctionLines.junctionH}
            depth={depth}
            strokeW={strokeW}
            opacity={opacity}
            spokeAngles={junctionLines.spokeAngles}
          />
        )}
      </group>
    );
  }

  if (skipStraightRailMesh) {
    return null;
  }
  const { span, centerLocalX, centerLocalZ, alongX } = straightSpan;
  return (
    <group
      position={[centerLocalX, RAIL_LIFT, centerLocalZ]}
      rotation={alongX ? [0, 0, 0] : [0, Math.PI / 2, 0]}
    >
      <DualRailStraight3D
        length={span}
        trackWidth={strokeW}
        depth={depth}
        opacity={opacity}
        glow="done"
      />
    </group>
  );
};

const SchematicElement3D: React.FC<Props> = ({
  element,
  isConflict,
  storedFoupsCount,
  simulationFrames,
  layoutElements,
  skipStraightRailMesh = false,
  floorPlanScaleFactor,
  dxfUnitsPerMeter,
  hasFloorCalibration = false,
}) => {
  const { width: w, height: h } = element.dimensions;
  const fill =
    element.type === 'text'
      ? element.metadata.color || '#111827'
      : element.metadata.color || '#cccccc';
  const opacity = element.metadata.opacity ?? 1;
  const rotY = -(element.rotation * Math.PI) / 180;
  const groupY = getElement3DGroupY(element, undefined, useRailElevation());
  const depth = getElement3DExtrusionDepth(element);
  const railType = resolveRailType(element);

  const elements = layoutElements ?? EMPTY_LAYOUT_ELEMENTS;

  const scaleFactor = hasFloorCalibration ? floorPlanScaleFactor : undefined;
  const dxfUnits = hasFloorCalibration ? dxfUnitsPerMeter : undefined;
  const strokeW = getTrackStrokeWidth(element, scaleFactor, dxfUnits);

  const curveMesh = useMemo(() => {
    if (!ALL_RAIL_TYPES.has(element.type) || !isCurveRailType(railType)) return null;
    const radius = getCurveRadius(w, h);
    // Safety cap so tiny-radius curves don't become solid disks.
    const finalTrackWidth = Math.min(strokeW, radius * 0.8);
    const tubeRadius = Math.max(TRACK_3D_MIN_THICKNESS / 2, finalTrackWidth / 2);
    const scaleZ = depth / Math.max(finalTrackWidth, 1e-3);
    const angle =
      (getCurveSweepSign(element) * getCurveSweepAngle(railType) * Math.PI) / 180;

    return { radius, tubeRadius, angle, scaleZ };
  }, [element, railType, w, h, depth, strokeW]);

  const junctionLines = useMemo(() => {
    if (!ALL_RAIL_TYPES.has(element.type) || !isJunctionRailType(railType)) return null;
    const lineWidth = getTrackStrokeWidth(element);
    let lines: [number, number, number][][] = [];

    if (railType === 'junction-circle') {
      const maxPorts = getCircleJunctionMaxPorts(element);
      const spokes = getCircleJunctionSpokesLocal(w, h, maxPorts);
      const cx = w / 2;
      const cy = h / 2;
      const ringR = getCircleJunctionRingRadius(w, h);

      // Only the center turntable bridge is drawn; incoming rails meet at port tips.
      lines = [];

      const spokeAngles = spokes.map((s) => s.angle);

      return {
        lines,
        lineWidth,
        ringR,
        junctionW: w,
        junctionH: h,
        cx,
        cy,
        isTurntable: true as const,
        spokeAngles,
      };
    }
    return { lines, lineWidth };
  }, [element, railType, w, h, elements]);

  // OHT: attach directly to rail centerline and rotate with the track.
  const ohtRailPose = useMemo(() => {
    if (element.type !== 'oht-vehicle') return null;
    const { pivotX, pivotY } = getOhtPivot(element, w, h);
    const pivotWorld = getOhtPivotWorld(element, w, h);
    let closestX = pivotWorld.x;
    let closestY = pivotWorld.y;
    let closestAngle = element.rotation || 0;
    let minDist = Infinity;

    Object.values(elements).forEach((el) => {
      if (!ALL_RAIL_TYPES.has(el.type)) return;
      const rx = el.position.x;
      const ry = el.position.y;
      const rw = el.dimensions.width;
      const rh = el.dimensions.height;
      const alongX = rw >= rh;
      const rotY = -((el.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(rotY);
      const sin = Math.sin(rotY);

      // Local centerline endpoints matching 3D rail geometry
      const lx1 = alongX ? 0 : rw / 2;
      const lz1 = alongX ? rh / 2 : 0;
      const lx2 = alongX ? rw : rw / 2;
      const lz2 = alongX ? rh / 2 : rh;

      // Transform to Three.js world X/Z plane (where Z is canvas Y)
      const segStartX = rx + lx1 * cos + lz1 * sin;
      const segStartY = ry - lx1 * sin + lz1 * cos;
      const segEndX = rx + lx2 * cos + lz2 * sin;
      const segEndY = ry - lx2 * sin + lz2 * cos;

      const dx = segEndX - segStartX;
      const dy = segEndY - segStartY;
      const lenSq = dx * dx + dy * dy;
      let t = 0;
      if (lenSq > 0) {
        t = ((pivotWorld.x - segStartX) * dx + (pivotWorld.y - segStartY) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
      }
      const projX = segStartX + t * dx;
      const projY = segStartY + t * dy;
      const distSq =
        (pivotWorld.x - projX) * (pivotWorld.x - projX) +
        (pivotWorld.y - projY) * (pivotWorld.y - projY);
      if (distSq < minDist) {
        minDist = distSq;
        closestX = projX;
        closestY = projY;
        closestAngle = alongX ? (el.rotation || 0) : (el.rotation || 0) + 90;
      }
    });

    // Snap onto rail if within 500 world units (or fallback to pivotWorld)
    if (minDist < 250000) {
      return { x: closestX, y: closestY, pivotX, pivotY, angle: closestAngle };
    }
    return { x: pivotWorld.x, y: pivotWorld.y, pivotX, pivotY, angle: element.rotation || 0 };
  }, [element, elements, w, h]);

  const lineLike = isLineLikeRectangle(element, w, h);

  // Straight rails and curves are drawn by InstancedTrack — skip straight rail mesh only.
  if (
    skipStraightRailMesh &&
    ALL_RAIL_TYPES.has(element.type) &&
    !isJunctionRailType(railType) &&
    !isConnectorRailType(railType)
  ) {
    return null;
  }

  if (ohtRailPose) {
    const ohtRotY = -((ohtRailPose.angle ?? (element.rotation || 0)) * Math.PI) / 180;
    return (
      <group position={[ohtRailPose.x, groupY, ohtRailPose.y]} rotation={[0, ohtRotY, 0]}>
        <group position={[-ohtRailPose.pivotX, 0, -ohtRailPose.pivotY]}>
          <OhtVehicle3D
            element={element}
            w={w}
            h={h}
            depth={depth}
            fill={fill}
            opacity={opacity}
          />
          {isConflict && (
            <mesh position={[w / 2, depth / 2, h / 2]} scale={[w + 10, depth + 10, h + 10]}>
              <boxGeometry />
              <meshBasicMaterial color="#ef4444" wireframe />
            </mesh>
          )}
        </group>
      </group>
    );
  }

  return (
    <group position={[element.position.x, groupY, element.position.y]} rotation={[0, rotY, 0]}>
      {ALL_RAIL_TYPES.has(element.type) && isConnectorRailType(railType) ? (
        <ConnectorRail3D
          element={element}
          w={w}
          h={h}
          depth={depth}
          opacity={opacity}
          strokeW={strokeW}
        />
      ) : ALL_RAIL_TYPES.has(element.type) ? (
        <Suspense
          fallback={
            curveMesh ? (
              <mesh
                position={[0, RAIL_LIFT, 0]}
                rotation={[Math.PI / 2, 0, 0]}
                scale={[1, 1, curveMesh.scaleZ]}
              >
                <torusGeometry args={[curveMesh.radius, curveMesh.tubeRadius, 8, 24, curveMesh.angle]} />
                <meshBasicMaterial
                  color={fill}
                  transparent={opacity < 1}
                  opacity={opacity}
                  toneMapped={false}
                />
              </mesh>
            ) : (
              <mesh position={[w / 2, RAIL_LIFT, h / 2]}>
                <boxGeometry args={[w, depth, strokeW]} />
                <meshBasicMaterial
                  color={fill}
                  transparent={opacity < 1}
                  opacity={opacity}
                  toneMapped={false}
                />
              </mesh>
            )
          }
        >
          <OverheadRail3D
            element={element}
            layoutRails={Object.values(elements)}
            w={w}
            h={h}
            depth={depth}
            opacity={opacity}
            strokeW={strokeW}
            curveMesh={curveMesh}
            junctionLines={junctionLines}
            skipStraightRailMesh={skipStraightRailMesh}
          />
        </Suspense>
      ) : element.type === 'stocker' ? (
        <Suspense
          fallback={
            <FloorRect3D
              w={w}
              h={h}
              depth={depth}
              fill={FACTORY_3D_PALETTE.storage.body}
              opacity={opacity}
            />
          }
        >
          <AmhsStockerFromLayout3D
            w={w}
            h={h}
            opacity={opacity}
            bayCount={element.customProperties?.bayCount as string | number}
            outsideStocker={element.customProperties?.outsideStocker === true}
            storedFoupsCount={storedFoupsCount}
            elementId={element.id}
            label={element.metadata?.name || 'Stocker'}
          />
        </Suspense>
      ) : element.type === 'stb' ? (
        <Suspense
          fallback={
            <EquipmentFloorMesh type="stb" w={w} h={h} d={depth} opacity={opacity} />
          }
        >
          <AmhsLoadPortFromLayout3D
            w={w}
            h={h}
            depth={depth}
            opacity={opacity}
            label={element.metadata?.name || 'STB'}
            element={element}
            simulationFrames={simulationFrames}
          />
        </Suspense>
      ) : element.type === 'ohb' ? (
        <AmhsEquipmentFromLayout3D
          w={w}
          h={h}
          depth={depth}
          opacity={opacity}
          element={element}
          type="AMHS_INTERFACE"
          loadPortCount={2}
          label={element.metadata?.name || 'OHB'}
          simulationFrames={simulationFrames}
        />
      ) : element.type === 'lifter' ? (
        <AmhsEquipmentFromLayout3D
          w={w}
          h={h}
          depth={depth}
          opacity={opacity}
          element={element}
          type="AMHS_INTERFACE"
          loadPortCount={2}
          label={element.metadata?.name || 'LIFTER'}
          simulationFrames={simulationFrames}
        />
      ) : isDxfEquipmentLayoutElement(element) ? (
        <Suspense
          fallback={
            <FloorRect3D
              w={w}
              h={h}
              depth={depth}
              fill={fill !== '#cccccc' ? fill : FACTORY_3D_PALETTE.fabEquipment.body}
              opacity={opacity}
            />
          }
        >
          <AmhsEquipmentFromLayout3D
            w={w}
            h={h}
            depth={depth}
            opacity={opacity}
            element={element}
            label={element.metadata?.name}
            simulationFrames={simulationFrames}
          />
        </Suspense>
      ) : element.type === 'circle' ? (
        <mesh position={[w / 2, depth / 2, h / 2]}>
          <cylinderGeometry args={[Math.min(w, h) / 2, Math.min(w, h) / 2, depth, 24]} />
          <meshBasicMaterial
            color={fill}
            transparent={opacity < 1}
            opacity={opacity}
            toneMapped={false}
          />
        </mesh>
      ) : element.type === 'text' ? (
        <TextLabel3D
          text={element.metadata.name || 'Label'}
          capHeight={Math.max(8, Math.min(h * 0.55, 12))}
          fill={fill}
          opacity={opacity}
        />
      ) : lineLike ? (
        <LineRect3D w={w} h={h} depth={depth} fill={fill} opacity={opacity} />
      ) : (
        <FloorRect3D w={w} h={h} depth={depth} fill={fill} opacity={opacity} />
      )}

      {isConflict && (
        <mesh position={[w / 2, depth / 2, h / 2]}>
          <boxGeometry args={[w + 6, depth + 4, h + 6]} />
          <meshBasicMaterial color="#ef4444" wireframe transparent opacity={0.85} />
        </mesh>
      )}
    </group>
  );
};

export default React.memo(SchematicElement3D);
